import { FunctionComponent, useEffect } from "react";
import { Button } from "@mantine/core";
import Link from "next/link";
import useUserSendingme from "@form/hooks/useUserSendingme";
import useVerifyCampaign from "@form/hooks/useVerifyCampaign";
import mixpanel from "mixpanel-browser";
import useAccount from "@/hooks/useAccount";

import type { SendingmeForm } from "@form/components/form/modules/sendingme";
import type { SubmitError } from "@form/hooks/useVerifyCampaign";

const SendingmeVerify: FunctionComponent<{
  form: SendingmeForm;
  available: boolean;
  needVerify: boolean;
  submitError?: SubmitError;
}> = ({ form, available, needVerify, submitError }) => {
  const { login, isLogin } = useAccount();
  const { sendingme, connect, connecting, authLink } = useUserSendingme();
  const { changeVerifyState, result } = useVerifyCampaign(`sendingme`);

  const isConnected = isLogin && !!sendingme && available;

  const showConnectError = needVerify && !result.sendingme;

  useEffect(() => {
    changeVerifyState(isConnected);
  }, [isConnected]);

  let verifyCount = 0;

  if (form.values.needJoinSquad && form.values.squads) {
    verifyCount++;
  }

  return (
    <div className={available ? "" : "relative opacity-40 disable"}>
      <div className="flex items-center">
        <div className="flex-1">
          <div className="flex items-center">
            {form.module.icon && (
              <form.module.icon className="w-5 h-5"></form.module.icon>
            )}
            <span className="text-body text-dark1 font-bold ml-2 dark:text-gray_f9">
              Sending.Me
            </span>
            {showConnectError && !sendingme && !isConnected && (
              <span className="text-error text-sm ml-2">This is required</span>
            )}
            {sendingme && isConnected && (
              <span className="text-bodyS text-gray_58 ml-2 dark:text-[#DCECFA]">
                @
              </span>
            )}
            <div className="flex-1"></div>
            {isConnected && (
              <span className="text-green text-bodyS font-bold dark:text-dark_green">
                Connected
              </span>
            )}
          </div>
        </div>
        {!isConnected && (
          <div className="flex-shrink-0 ml-4">
            <a
              href={authLink}
              rel="noreferrer"
              className="block"
              target="_blank"
              onClick={(e) => {
                if (!isLogin) {
                  login();
                  e.preventDefault();
                  return false;
                }
                if (!authLink) {
                  e.preventDefault();
                  return false;
                }
                mixpanel.track("connect_sendingme");
                connect();
              }}
            >
              <Button
                loading={connecting}
                variant="outline"
                radius="xl"
                size="md"
              >
                Connect
              </Button>
            </a>
          </div>
        )}
      </div>
      <div
        className={`mt-2 ${isConnected ? "" : "hidden"} ${
          verifyCount > 1 ? "grid grid-cols-2 gap-2 " : ""
        }`}
      >
        {form.values.needJoinSquad &&
          form.values.squads &&
          form.values.squads.map((squad, index) => {
            const joinError =
              submitError?.[form.values.uid]?.needJoinSquad || [];
            const showError = joinError.includes(squad);
            return (
              <div key={index}>
                <div
                  className={`py-2 px-4 bg-gray_f9 rounded-lg flex items-center justify-between dark:bg-[#192844] ${
                    showError ? "bg-error/10 border border-error" : ""
                  }`}
                >
                  <div className="flex items-center text-body text-dark1 flex-1 flex-wrap dark:text-gray_f9">
                    <span className="flex-shrink-0">Join this</span>
                    <Link passHref href={squad}>
                      <a
                        className="text-[#47679A] underline mx-1 flex-1 text-ellipsis overflow-hidden dark:text-[#87B5FF]"
                        target="_black"
                      >
                        squad
                      </a>
                    </Link>
                  </div>
                  {/* <CheckIcon className={`flex-shrink-0 ml-2 ${accountVerified ? 'fill-green' : ''}`}></CheckIcon> */}
                </div>
                <div
                  className={`text-error text-sm mt-1 ${
                    showError ? "flex" : "hidden"
                  }`}
                >
                  This is required
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default SendingmeVerify;
